import * as React from "react";
import { CalendarDays, MapPin, PlayCircle, Shield } from "lucide-react";
import { Button } from "@/v2/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/v2/components/ui/dialog";
import { useCountdown } from "./useCountdown";

/**
 * The pre-game screen: who is playing, when, and how long until it starts.
 *
 * This is the v2 replacement for the flip-clock screen the v1 watch page shows
 * before a stream is available (src/Pages/Watch/index.tsx). It is purely
 * presentational: every field arrives as a prop, already resolved, so the one
 * rule the design carries can be applied uniformly — A FIELD WE DO NOT HAVE IS
 * A FIELD WE DO NOT SHOW. No placeholder records, no empty venue, no "TBD".
 * Anything nullable here is a section that can be absent, and each one checks
 * for itself rather than rendering a hole.
 *
 * SPACING IS INLINE, NOT TAILWIND. Inside `.v2` the global Bootstrap reset
 * (`* { padding: 0 }`, unlayered) outranks Tailwind's utilities layer, so
 * `p-6`/`py-12` resolve to 0, and margins are reset to 0 on buttons, `p` and
 * headings. See src/v2/styles/v2.css. Borders are inline for the same reason:
 * the scoped preflight sets `border-width: 0` on button/input/hr, so a
 * `border-t` on an `hr` draws nothing.
 */

export type CountdownBrand = {
  /** Tenant mark (config.logoUrl, the one the sign-in card shows). */
  logo?: string | null;
  /** The name to fall back to, and the logo's alt text. */
  name?: string | null;
};

export type CountdownTeam = {
  /** Full display name, e.g. "Minot Minotauros". */
  name: string;
  logo?: string | null;
  /** Season record as upstream phrases it ("8-3-1"), or null when unknown. */
  record?: string | null;
  /**
   * The record is still being fetched. Distinct from `record: null`, which
   * means "there is none": one holds the space with a shimmer, the other
   * leaves no space at all.
   */
  recordLoading?: boolean;
};

export type GamePreviewContent = {
  /** Headline for the preview card. */
  title?: string | null;
  /** The teaser paragraph shown on the card. */
  summary?: string | null;
  /** The full article, revealed in place by "Read Preview". */
  full?: string | null;
};

export type CountdownInfoItem = {
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  label: string;
  /** Lines under the label. Empty entries are dropped, not rendered blank. */
  lines: Array<string | null | undefined>;
};

function TeamColumn({ team }: { team: CountdownTeam }) {
  return (
    <div
      className="flex flex-1 flex-col items-center text-center"
      style={{ gap: "10px", minWidth: 0 }}
    >
      <div
        className="flex items-center justify-center"
        style={{ height: "92px" }}
      >
        {team.logo ? (
          <img
            src={team.logo}
            alt=""
            style={{ maxHeight: "92px", maxWidth: "140px", objectFit: "contain" }}
          />
        ) : (
          <Shield className="size-16 text-slate-300" aria-hidden="true" />
        )}
      </div>
      <div className="flex flex-col items-center" style={{ gap: "3px" }}>
        <div className="text-base font-bold text-slate-900" style={{ lineHeight: 1.2 }}>
          {team.name}
        </div>
        {/* No record, no line. An empty record reads as a team that has not
            played, which is a different claim from "we were not told". A
            record still in flight holds its space with a shimmer instead, so
            the name does not shift when it lands. */}
        {team.recordLoading ? (
          <div
            className="v2-skeleton-wave"
            style={{ width: 64, height: 12, borderRadius: 6 }}
            aria-hidden="true"
          />
        ) : team.record ? (
          <div className="text-sm text-slate-400" style={{ letterSpacing: "0.08em" }}>
            {team.record.split("-").join(" - ")}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function ClockCell({ value, label }: { value: number; label: string }) {
  return (
    <div
      className="flex flex-col items-center rounded-lg"
      style={{
        backgroundColor: "var(--v2-accent)",
        padding: "12px 18px",
        minWidth: "84px",
        gap: "2px",
      }}
    >
      <div
        className="text-3xl font-bold tabular-nums"
        style={{ color: "var(--v2-action)", lineHeight: 1.1 }}
      >
        {String(value).padStart(2, "0")}
      </div>
      <div
        className="text-[10px] font-semibold uppercase text-slate-400"
        style={{ letterSpacing: "0.12em" }}
      >
        {label}
      </div>
    </div>
  );
}

function InfoItem({ item }: { item: CountdownInfoItem }) {
  const Icon = item.icon;
  const lines = item.lines.filter((line): line is string => Boolean(line && line.trim()));
  return (
    <div className="flex items-start justify-center" style={{ gap: "10px" }}>
      <Icon className="size-5 shrink-0 text-slate-500" style={{ marginTop: "2px" }} />
      <div className="flex flex-col text-left" style={{ gap: "2px" }}>
        <div className="text-sm font-bold text-slate-900" style={{ lineHeight: 1.2 }}>
          {item.label}
        </div>
        {lines.map((line) => (
          <div key={line} className="text-[13px] text-slate-500" style={{ lineHeight: 1.35 }}>
            {line}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Shifts a hex colour toward black (negative) or white (positive).
 *
 * Enough colour maths to build a gradient out of one brand hex, and no more:
 * the alternative is shipping a colour library to darken two values.
 * Non-hex input returns unchanged, so a tenant that stores "rgb(...)" degrades
 * to a flat band rather than a broken one.
 */
function shade(hex: string, amount: number): string {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return hex;
  const full =
    m[1].length === 3
      ? m[1].split("").map((c) => c + c).join("")
      : m[1];
  const channels = [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16));
  const mixed = channels.map((c) => {
    const target = amount > 0 ? 255 : 0;
    const v = Math.round(c + (target - c) * Math.abs(amount));
    return Math.max(0, Math.min(255, v));
  });
  return `#${mixed.map((c) => c.toString(16).padStart(2, "0")).join("")}`;
}

/**
 * The matchup art for a preview that has no image of its own.
 *
 * The two teams' brand colours, each rendered as a lit face rather than a flat
 * fill: a dark-to-light ramp per side, a soft glow behind each crest, a lit
 * seam where they meet, and a vignette to seat the whole thing. Built in the
 * browser from data the page already holds, so it costs nothing per game and
 * cannot fail, which is what makes it the floor under any generated image
 * rather than a competitor to one.
 *
 * A team's colour is an empty string far more often than not, so the caller
 * passes the tenant's own two colours as the fallback. When both sides still
 * land on the same hex the seam would vanish, so one side is shifted to keep
 * the split legible.
 */
function MatchupHeroArt({
  awayTeam,
  homeTeam,
  awayColor,
  homeColor,
}: {
  awayTeam: CountdownTeam;
  homeTeam: CountdownTeam;
  awayColor?: string | null;
  homeColor?: string | null;
}) {
  const left = awayColor || "#1e293b";
  const rawRight = homeColor || "#475569";
  // Two identical hexes would render as one flat field with no matchup in it.
  const right =
    rawRight.toLowerCase() === left.toLowerCase() ? shade(rawRight, -0.35) : rawRight;

  return (
    <div
      className="relative flex items-center justify-center overflow-hidden"
      style={{
        minHeight: "180px",
        height: "100%",
        gap: "22px",
        padding: "20px",
        backgroundColor: left,
        backgroundImage: [
          // Crest glows, so each logo sits in its own pool of light.
          "radial-gradient(60% 70% at 26% 46%, rgba(255,255,255,0.26), rgba(255,255,255,0) 70%)",
          "radial-gradient(60% 70% at 76% 54%, rgba(255,255,255,0.20), rgba(255,255,255,0) 70%)",
          // The lit seam: a thin bright edge on the diagonal where they meet.
          "linear-gradient(112deg, rgba(255,255,255,0) 46%, rgba(255,255,255,0.38) 50%, rgba(255,255,255,0) 54%)",
          // Seats the art: darker at the corners than through the middle.
          "radial-gradient(120% 120% at 50% 40%, rgba(0,0,0,0) 40%, rgba(0,0,0,0.38) 100%)",
          // The two brand faces, each ramped light-to-dark across its half.
          `linear-gradient(112deg, ${shade(left, 0.16)} 0%, ${left} 28%, ${shade(left, -0.22)} 49.6%, ${shade(right, -0.22)} 50.4%, ${right} 72%, ${shade(right, 0.16)} 100%)`,
        ].join(", "),
      }}
      aria-hidden="true"
    >
      {[awayTeam, homeTeam].map((team, i) =>
        team.logo ? (
          <img
            key={i}
            src={team.logo}
            alt=""
            style={{
              maxHeight: "88px",
              maxWidth: "40%",
              objectFit: "contain",
              filter: "drop-shadow(0 4px 10px rgba(0,0,0,0.45))",
            }}
          />
        ) : null,
      )}
    </div>
  );
}

function GamePreviewCardSkeleton() {
  return (
    <div
      className="v2-countdown-preview-card flex overflow-hidden rounded-xl bg-white"
      style={{ border: "1px solid var(--v2-border)" }}
    >
      <div
        className="v2-countdown-preview-art v2-skeleton-wave shrink-0"
        style={{ width: "38%", minWidth: "220px", minHeight: "180px" }}
        aria-hidden="true"
      />
      <div
        className="flex flex-1 flex-col justify-center"
        style={{ padding: "22px 26px", gap: "12px", minWidth: 0 }}
      >
        <div className="v2-skeleton-wave" style={{ width: 110, height: 10, borderRadius: 5 }} />
        <div className="v2-skeleton-wave" style={{ width: "70%", height: 20, borderRadius: 6 }} />
        <div className="v2-skeleton-wave" style={{ width: "100%", height: 12, borderRadius: 6 }} />
        <div className="v2-skeleton-wave" style={{ width: "92%", height: 12, borderRadius: 6 }} />
        <div className="v2-skeleton-wave" style={{ width: 140, height: 32, borderRadius: 6 }} />
      </div>
    </div>
  );
}

function GamePreviewCard({
  preview,
  imageUrl,
  awayTeam,
  homeTeam,
  awayColor,
  homeColor,
}: {
  preview: GamePreviewContent;
  imageUrl?: string | null;
  awayTeam: CountdownTeam;
  homeTeam: CountdownTeam;
  awayColor?: string | null;
  homeColor?: string | null;
}) {
  const [open, setOpen] = React.useState(false);

  const title = preview.title?.trim() || null;
  const full = preview.full?.trim() || null;
  /*
   * The teaser. Upstream writes a short form for some games and not for others
   * (PreGameShortSummary is simply absent on most rows), so the article itself
   * is the fallback and CSS clamps it to three lines. Cutting the string here
   * instead would mean guessing a character count that is wrong at every width.
   */
  const teaser = preview.summary?.trim() || full;

  // Nothing written means no card. The design puts the preview last precisely
  // because most games will not have one.
  if (!title && !teaser) return null;

  return (
    <>
      <div
        className="v2-countdown-preview-card flex overflow-hidden rounded-xl bg-white shadow-sm"
        style={{ border: "1px solid var(--v2-border)" }}
      >
        <div
          className="v2-countdown-preview-art shrink-0"
          style={{ width: "38%", minWidth: "220px" }}
        >
          {imageUrl ? (
            <img
              src={imageUrl}
              alt=""
              style={{ width: "100%", height: "100%", minHeight: "180px", objectFit: "cover" }}
            />
          ) : (
            <MatchupHeroArt
              awayTeam={awayTeam}
              homeTeam={homeTeam}
              awayColor={awayColor}
              homeColor={homeColor}
            />
          )}
        </div>

        <div
          className="flex flex-1 flex-col justify-center"
          style={{ padding: "22px 26px", gap: "10px", minWidth: 0 }}
        >
          <div
            className="text-[11px] font-bold uppercase text-slate-400"
            style={{ letterSpacing: "0.14em" }}
          >
            Game Preview
          </div>
          {title ? (
            <div className="text-xl font-bold text-slate-900" style={{ lineHeight: 1.25 }}>
              {title}
            </div>
          ) : null}
          {teaser ? (
            <div
              className="text-sm text-slate-600 line-clamp-3"
              style={{ lineHeight: 1.55 }}
            >
              {teaser}
            </div>
          ) : null}
          {/* Only when there is an article to open. A button that opens a
              dialog holding the same three lines already on the card would be
              a button that lies. */}
          {full ? (
            <div className="flex" style={{ marginTop: "4px" }}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setOpen(true)}
                style={{
                  border: "1px solid var(--v2-action)",
                  color: "var(--v2-action)",
                }}
              >
                Read Preview →
              </Button>
            </div>
          ) : null}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{title ?? "Game Preview"}</DialogTitle>
          </DialogHeader>
          {/* The article as written, paragraph breaks and all. Scrolls inside
              the dialog rather than growing it past the viewport. */}
          <div
            className="text-sm text-slate-600"
            style={{
              lineHeight: 1.65,
              whiteSpace: "pre-wrap",
              maxHeight: "60vh",
              overflowY: "auto",
              marginTop: "12px",
            }}
          >
            {full ?? teaser}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function GameCountdown({
  brand,
  awayTeam,
  homeTeam,
  awayTeamColor,
  homeTeamColor,
  /** The instant the clock counts down to (ms). Null hides the clock. */
  streamAvailableAtMs,
  gameDateLabel,
  gameTimeLabel,
  caption,
  venueName,
  venueDetail,
  seasonName,
  seasonDetail,
  preview,
  previewImageUrl,
  previewLoading = false,
  onComplete,
}: {
  brand?: CountdownBrand | null;
  awayTeam: CountdownTeam;
  homeTeam: CountdownTeam;
  awayTeamColor?: string | null;
  homeTeamColor?: string | null;
  streamAvailableAtMs: number | null;
  gameDateLabel?: string | null;
  gameTimeLabel?: string | null;
  caption?: string | null;
  venueName?: string | null;
  venueDetail?: string | null;
  seasonName?: string | null;
  seasonDetail?: string | null;
  preview?: GamePreviewContent | null;
  previewImageUrl?: string | null;
  /** The write-up is still being fetched; hold the card's space with a shimmer. */
  previewLoading?: boolean;
  onComplete?: () => void;
}) {
  const clock = useCountdown(streamAvailableAtMs, onComplete);

  /*
   * Only the items we actually know. The venue tile is dropped entirely on a
   * game with no facility rather than shown with an empty second line, and the
   * season tile likewise: these come from two different upstreams (the tenant's
   * player API and GameSheet) and most tenants have neither.
   */
  const infoItems: CountdownInfoItem[] = [];
  if (venueName) {
    infoItems.push({ icon: MapPin, label: venueName, lines: [venueDetail] });
  }
  if (seasonName) {
    infoItems.push({ icon: PuckIcon, label: seasonName, lines: [seasonDetail] });
  }
  if (gameTimeLabel || gameDateLabel) {
    infoItems.push({
      icon: CalendarDays,
      label: "Game Time",
      lines: [gameTimeLabel, gameDateLabel],
    });
  }

  return (
    <div
      className="v2-countdown flex flex-col items-center"
      style={{ padding: "28px 16px 48px", gap: "18px", width: "100%" }}
    >
      {/* The tenant's own mark, the same one the header carries. Its name is
          the fallback, so a tenant with no logo still says whose broadcast
          this is rather than opening on two crests and nothing else. */}
      {brand?.logo ? (
        <img
          src={brand.logo}
          alt={brand.name ?? ""}
          style={{ maxHeight: "56px", maxWidth: "200px", objectFit: "contain" }}
        />
      ) : brand?.name ? (
        <div
          className="text-[11px] font-semibold uppercase text-slate-400 text-center"
          style={{ letterSpacing: "0.18em" }}
        >
          {brand.name}
        </div>
      ) : null}

      <div
        className="v2-countdown-matchup flex items-start justify-center"
        style={{ gap: "16px", width: "100%", maxWidth: "620px" }}
      >
        <TeamColumn team={awayTeam} />
        <div
          className="flex items-center justify-center self-stretch"
          style={{
            borderLeft: "1px solid var(--v2-border)",
            borderRight: "1px solid var(--v2-border)",
            padding: "0 26px",
            minHeight: "92px",
          }}
        >
          <span className="text-sm font-bold uppercase text-slate-400" style={{ letterSpacing: "0.08em" }}>
            vs
          </span>
        </div>
        <TeamColumn team={homeTeam} />
      </div>

      <div className="flex flex-col items-center" style={{ gap: "2px" }}>
        {gameDateLabel ? (
          <div
            className="text-xs font-semibold uppercase text-slate-400"
            style={{ letterSpacing: "0.16em" }}
          >
            {gameDateLabel}
          </div>
        ) : null}
        {gameTimeLabel ? (
          <div
            className="text-4xl font-bold text-slate-900"
            style={{ lineHeight: 1.2 }}
          >
            {gameTimeLabel}
          </div>
        ) : null}
        {caption ? (
          <div className="text-sm font-semibold text-slate-500">{caption}</div>
        ) : null}
      </div>

      {streamAvailableAtMs != null ? (
        <div
          className="v2-countdown-clock flex flex-wrap items-center justify-center"
          style={{ gap: "14px" }}
          role="timer"
          aria-live="off"
        >
          <ClockCell value={clock.days} label="Days" />
          <ClockCell value={clock.hours} label="Hours" />
          <ClockCell value={clock.minutes} label="Minutes" />
          <ClockCell value={clock.seconds} label="Seconds" />
        </div>
      ) : null}

      <div className="flex items-center text-center" style={{ gap: "8px" }}>
        <PlayCircle className="size-5 shrink-0" style={{ color: "var(--v2-action)" }} />
        <span className="text-sm text-slate-600">
          The stream will begin automatically when available.
        </span>
      </div>

      {infoItems.length > 0 ? (
        <div
          className="v2-countdown-info flex flex-wrap items-start justify-center"
          style={{
            borderTop: "1px solid var(--v2-border)",
            paddingTop: "20px",
            marginTop: "6px",
            gap: "0px",
            width: "100%",
            maxWidth: "760px",
          }}
        >
          {infoItems.map((item, i) => (
            <div
              key={item.label}
              className="v2-countdown-info-item flex flex-1 justify-center"
              style={{
                padding: "0 22px",
                borderLeft: i === 0 ? undefined : "1px solid var(--v2-border)",
                minWidth: "200px",
              }}
            >
              <InfoItem item={item} />
            </div>
          ))}
        </div>
      ) : null}

      {previewLoading ? (
        <div style={{ width: "100%", maxWidth: "860px", marginTop: "10px" }}>
          <GamePreviewCardSkeleton />
        </div>
      ) : preview ? (
        <div style={{ width: "100%", maxWidth: "860px", marginTop: "10px" }}>
          <GamePreviewCard
            preview={preview}
            imageUrl={previewImageUrl}
            awayTeam={awayTeam}
            homeTeam={homeTeam}
            awayColor={awayTeamColor}
            homeColor={homeTeamColor}
          />
        </div>
      ) : null}
    </div>
  );
}

/**
 * A puck. lucide has no hockey icon and the season line reads as a stray CD
 * with any of its discs, so this is eight lines of SVG instead of a dependency.
 */
function PuckIcon({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg viewBox="0 0 24 24" className={className} style={style} aria-hidden="true">
      <ellipse cx="12" cy="9" rx="9" ry="3.5" fill="currentColor" />
      <path d="M3 9v5c0 1.93 4.03 3.5 9 3.5s9-1.57 9-3.5V9c0 1.93-4.03 3.5-9 3.5S3 10.93 3 9z" fill="currentColor" />
    </svg>
  );
}

export default GameCountdown;
